<template>
  <ui-modal-layout :title="tr('Ad details')">
    <template #toolbar>
      <form-step-tab v-model="activeTab" :items="tabs" equal-width />
    </template>

    <template v-if="activeTab === 0">
      <info-text v-if="isRejected" type="error">
        {{ tr('Meta has rejected this ad, so it is not delivering.') }}
      </info-text>
      <info-text v-else-if="isDeleted">
        {{ tr('This ad has been deleted, so it can no longer be modified.') }}
      </info-text>

      <div class="grid grid-cols-2 gap-3">
        <div class="flex flex-col gap-1">
          <span class="text-xs font-semibold text-muted-foreground">{{ tr('Created') }}</span>
          <span>{{ formatDateAndTime(ad.created_at) }}</span>
        </div>
        <div class="flex flex-col gap-1">
          <span class="text-xs font-semibold text-muted-foreground">{{ tr('Status') }}</span>
          <span class="inline-flex items-center gap-2">
            <switcher
              :model-value="isAdActive(ad)"
              :disabled="isAdSwitchDisabled(ad)"
              :loading="isUpdatingStatus"
              @update:model-value="(val) => onChangeAdStatus(ad, val)"
            />
            {{ statusLabel }}
          </span>
        </div>
      </div>

      <section class="flex flex-col gap-1.5">
        <div class="flex items-center justify-between gap-2">
          <h3 class="m-0 flex flex-wrap items-center gap-x-2 text-sm font-semibold text-foreground">
            {{ tr('Ad text') }}
            <span v-if="ad.created_with_ai" class="inline-flex items-center gap-1 text-xs font-normal text-primary">
              <sparkles-icon class="size-3.5" />
              {{ tr('AI-generated variant') }} - v{{ ad.variation }}
            </span>
          </h3>
          <ui-button
            v-if="!readonlyMode && !isDeleted"
            variant="outline"
            size="xs"
            :disabled="!canEditAdText || isUpdatingStatus"
            @click="() => openEditModal()"
          >
            <pencil-icon />
            {{ tr('Edit') }}
          </ui-button>
        </div>
        <read-more-text :text="adText" />
      </section>

      <section class="glass-tile flex flex-col rounded-xl px-4 py-2">
        <h3 class="m-0 flex items-center gap-2 py-2 text-sm font-semibold text-foreground">
          <rule-type-badge v-if="ad.created_by_rule" :action="ad.created_by_rule.action" compact />
          <span class="truncate">{{ automationTitle }}</span>
        </h3>
        <dl class="m-0 flex flex-col divide-y divide-border">
          <div v-if="brand?.id" class="flex items-center justify-between gap-3 py-2.5">
            <dt>{{ tr('Brand') }}</dt>
            <dd class="m-0">
              <router-link
                :to="`/brands/${brand.id}`"
                class="flex items-center gap-2 font-semibold text-primary hover:underline"
                @click="() => $fullscreenModal.closeAll()"
              >
                <social-page :source-profiles="brandSourceProfiles" only-icon />
                {{ brand.name }}
              </router-link>
            </dd>
          </div>
          <div v-if="objectiveLabel" class="flex items-center justify-between gap-3 py-2.5">
            <dt>{{ tr('Objective') }}</dt>
            <dd class="m-0 text-right">{{ tr(objectiveLabel) }}</dd>
          </div>
          <div class="flex items-center justify-between gap-3 py-2.5">
            <dt>{{ tr('Creation method') }}</dt>
            <dd :class="[ 'm-0 text-right', creativeSourceLabel ? '' : 'text-muted-foreground italic' ]">
              {{ creativeSourceLabel || tr('Unknown') }}
            </dd>
          </div>
          <div v-if="placements.length" class="flex items-center justify-between gap-3 py-2.5">
            <dt>{{ tr('Placements') }}</dt>
            <dd class="m-0 flex flex-wrap justify-end gap-2">
              <i
                v-for="platform in placements"
                :key="platform"
                v-tooltip="ucfirst(platform)"
                :class="[ 'icon size-4', platform ]"
              />
            </dd>
          </div>
          <div v-if="budgetText" class="flex items-center justify-between gap-3 py-2.5">
            <dt>{{ budgetLabel }}</dt>
            <dd class="m-0 text-right tabular-nums">{{ budgetText }}</dd>
          </div>
        </dl>
        <div v-if="!isPublic && (ad.url || ad.created_by_rule)" class="flex flex-wrap gap-x-4 gap-y-2 border-t border-border py-3">
          <a
            v-if="ad.url"
            :href="ad.url"
            target="_blank"
            class="inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:underline"
          >
            <external-link-icon class="size-4" />
            {{ tr('View Ad in Meta Ads Manager') }}
          </a>
          <router-link
            v-if="ad.created_by_rule"
            :to="`/automation/${ad.created_by_rule.id}`"
            class="inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:underline"
            @click="() => $fullscreenModal.closeAll()"
          >
            {{ tr('Go to Automation') }}
            <arrow-right-icon class="size-4" />
          </router-link>
        </div>
      </section>

      <section v-if="ad.post" class="glass-tile flex flex-col rounded-xl px-4 py-2">
        <h3 class="m-0 py-2 text-sm font-semibold text-foreground">{{ tr('Source post') }}</h3>
        <inline-social-content :entity="ad.post" class="pb-2" />
        <dl class="m-0 flex flex-col divide-y divide-border border-t border-border">
          <div class="flex items-center justify-between gap-3 py-2.5">
            <dt>{{ tr('Posted') }}</dt>
            <dd class="m-0 text-right">{{ formatDateAndTime(ad.post.created_at) }}</dd>
          </div>
          <div v-if="ad.post.platform" class="flex items-center justify-between gap-3 py-2.5">
            <dt>{{ tr('Platform') }}</dt>
            <dd class="m-0 text-right">{{ ucfirst(ad.post.platform) }}</dd>
          </div>
        </dl>
        <div class="border-t border-border py-3">
          <router-link
            v-if="!isPublic"
            :to="`/posts/${ad.post.id}`"
            class="inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:underline"
            @click="() => $fullscreenModal.closeAll()"
          >
            {{ tr('Go to Post') }}
            <arrow-right-icon class="size-4" />
          </router-link>
          <button
            v-else
            type="button"
            data-slot="view-post"
            class="inline-flex cursor-pointer items-center gap-1.5 text-sm font-semibold text-primary hover:underline"
            @click="() => openSourcePost()"
          >
            {{ tr('View post') }}
            <arrow-right-icon class="size-4" />
          </button>
        </div>
      </section>
    </template>

    <template v-else-if="activeTab === 1">
      <div v-if="periods.length" class="flex flex-col gap-1.5">
        <span class="text-xs font-semibold text-muted-foreground">{{ tr('Period') }}</span>
        <ui-select v-model="metricPeriod" :disabled="isLoadingMetrics">
          <ui-select-trigger class="w-full sm:w-64" :aria-label="tr('Period')">
            <ui-select-value />
          </ui-select-trigger>
          <ui-select-content>
            <ui-select-item v-for="period in periods" :key="period.machine_name" :value="period.machine_name">
              {{ ucfirst(tr(period.label)) }}
            </ui-select-item>
          </ui-select-content>
        </ui-select>
      </div>

      <div v-if="isLoadingMetrics" class="grid grid-cols-2 gap-2.5 sm:grid-cols-3">
        <div v-for="i in 6" :key="'m-skel-' + i" class="glass-tile flex flex-col gap-2 rounded-xl p-3">
          <ui-skeleton class="h-3 w-3/5" />
          <ui-skeleton class="h-6 w-4/5" />
        </div>
      </div>

      <div v-else-if="metric" class="grid grid-cols-2 gap-2.5 sm:grid-cols-3">
        <div v-for="card in metricsCards" :key="card.key" class="glass-tile flex flex-col gap-1 rounded-xl p-3">
          <span class="flex items-center justify-between gap-1.5 text-xs font-semibold text-muted-foreground">
            {{ card.label }}
            <component :is="card.icon" class="size-4 shrink-0" />
          </span>
          <span class="text-lg font-bold text-foreground tabular-nums">
            {{ card.value }}<small v-if="card.suffix && card.value !== '–'" class="ml-0.5 text-xs">{{ card.suffix }}</small>
          </span>
        </div>
      </div>

      <p v-else class="m-0 text-muted-foreground">
        {{ tr('No performance data available.') }}
      </p>
    </template>

    <ad-preview
      v-else-if="activeTab === 2"
      :ad-id="ad.id"
      :ad-account-id="adAccountId"
      :token-part1="tokenPart1"
      :token-part2="tokenPart2"
      :scale="0.7"
    />
  </ui-modal-layout>
</template>

<script>
import { ArrowRight, ChartLine, CircleDollarSign, ExternalLink, Eye, MousePointerClick, Pencil, Sparkles } from '@lucide/vue';
import { markRaw } from 'vue';

import { AD_CREATIVE_TYPE } from '@/consts.js';
import Ad from '@/entities/Ad.js';
import AdStatusMixin from '@/mixins/AdStatus.js';
import AdService from '@/services/Ad.js';
import ReportService from '@/services/Report.js';
import AdDetailsEditModal from '@/ui/components/AdDetailsEditModal.vue';
import AdPreview from '@/ui/components/AdPreview.vue';
import FormStepTab from '@/ui/components/FormStepTab.vue';
import InfoText from '@/ui/components/InfoText.vue';
import InlineSocialContent from '@/ui/components/InlineSocialContent.vue';
import ReadMoreText from '@/ui/components/ReadMoreText.vue';
import ReportPostModal from '@/ui/components/ReportPostModal.vue';
import RuleTypeBadge from '@/ui/components/RuleTypeBadge.vue';
import SocialPage from '@/ui/components/SocialPage.vue';
import Switcher from '@/ui/components/Switcher.vue';
import { formatTime, formatToStringYMD } from '@/utils/date.js';
import { formatCompactNumber, formatNumber } from '@/utils/number.js';
import { ucfirst } from '@/utils/string.js';

export default {
  name: 'AdDetailsModal',
  components: {
    AdPreview,
    ArrowRightIcon: ArrowRight,
    ExternalLinkIcon: ExternalLink,
    FormStepTab,
    InfoText,
    InlineSocialContent,
    PencilIcon: Pencil,
    ReadMoreText,
    RuleTypeBadge,
    SocialPage,
    SparklesIcon: Sparkles,
    Switcher,
  },
  mixins: [ AdStatusMixin ],
  props: {
    ad: {
      type: Ad,
      required: true,
    },
    // Optional share-report tokens; present only in the public SharedReport flow.
    tokenPart1: {
      type: [ String, null ],
      required: false,
      default: () => null,
    },
    tokenPart2: {
      type: [ String, null ],
      required: false,
      default: () => null,
    },
  },
  data() {
    return {
      activeTab: 0,
      metric: null,
      metricPeriod: 'lifetime',
      periods: [],
      isLoadingMetrics: false,
    };
  },
  computed: {
    tabs() {
      return [
        this.tr('Details'),
        this.tr('Performance'),
        this.tr('Preview'),
      ];
    },
    isPublic() {
      return !this.$root.isLoggedIn;
    },
    readonlyMode() {
      return this.isPublic || this.$root.loggedInUser?.level === 'restricted_viewer';
    },
    adAccount() {
      return this.ad.ad_account ?? null;
    },
    adAccountId() {
      return this.adAccount?.id ?? null;
    },
    currency() {
      return this.adAccount?.currencyDetails ?? null;
    },
    brand() {
      const brandId = this.ad.post?.source_profile?.brand_id;
      if (!brandId) {
        return null;
      }

      const brands = this.$root.currentCompany?.brands ?? [];
      return brands.find(b => b.id === brandId) ?? null;
    },
    brandSourceProfiles() {
      const fromBrand = this.brand?.sourceProfiles ?? [];
      if (fromBrand.length) {
        return fromBrand;
      }

      const sourceProfile = this.ad.post?.source_profile;
      return sourceProfile ? [ sourceProfile ] : [];
    },
    isUpdatingStatus() {
      return this.isAdStatusUpdating(this.ad);
    },
    isDeleted() {
      return this.isAdDeleted(this.ad);
    },
    isRejected() {
      return this.isAdRejected(this.ad);
    },
    statusLabel() {
      if (this.isDeleted) {
        return this.tr('Deleted');
      }

      if (this.isRejected) {
        return this.tr('Rejected');
      }

      return this.isAdActive(this.ad) ? this.tr('Active') : this.tr('Paused');
    },
    adText() {
      return this.ad.text || this.ad.post?.text || '';
    },
    canEditAdText() {
      return Number(this.ad.creative_type) === AD_CREATIVE_TYPE.CUSTOM;
    },
    creativeSourceLabel() {
      switch (Number(this.ad.creative_type)) {
        case AD_CREATIVE_TYPE.CUSTOM:
          return this.tr('Ad creative');

        case AD_CREATIVE_TYPE.POST_BOOST:
          return this.tr('Post boost');

        default:
          return null;
      }
    },
    objectiveLabel() {
      const objective = this.ad.campaign?.objective;
      return this.$store.getters['app/facebookCampaignObjectiveLabel'](objective);
    },
    budget() {
      const campaign = this.ad.campaign;
      const adset = this.ad.adset;

      if (campaign && campaign.budget != null) {
        return { amount: campaign.budget, period: campaign.budget_period, scope: 'campaign' };
      }
      if (adset && adset.budget != null) {
        return { amount: adset.budget, period: adset.budget_period, scope: 'adset' };
      }

      return null;
    },
    budgetLabel() {
      if (!this.budget) {
        return this.tr('Budget');
      }

      let base;
      switch (this.budget.period) {
        case 'daily':
          base = this.tr('Daily budget');
          break;

        case 'lifetime':
          base = this.tr('Total budget');
          break;

        default:
          base = this.tr('Budget');
      }

      const scope = this.budget.scope === 'adset' ? this.tr('Ad set') : this.tr('Campaign');

      return base + ' (' + scope + ')';
    },
    budgetText() {
      if (!this.budget) {
        return null;
      }

      const symbol = this.currency?.symbol ?? '';
      const number = formatNumber(this.budget.amount, null, 0, true);

      return symbol.length === 1
        ? symbol + number
        : (number + (symbol ? ' ' + symbol : ''));
    },
    placements() {
      const platforms = this.ad.adset?.targeting_publisher_platforms;
      return Array.isArray(platforms) ? platforms : [];
    },
    automationTitle() {
      return this.ad.created_by_rule?.name || this.tr('Automatically created');
    },
    metricsCards() {
      const m = this.metric;
      if (!m) {
        return [];
      }

      const symbol = this.currency?.symbol ?? '';
      const cards = [
        { key: 'spend', label: this.tr('Spend'), icon: markRaw(CircleDollarSign), value: formatCompactNumber(m.spend), suffix: symbol },
        { key: 'impressions', label: this.tr('Impressions'), icon: markRaw(Eye), value: formatCompactNumber(m.impressions) },
        { key: 'reach', label: this.tr('Reach'), icon: markRaw(Eye), value: formatCompactNumber(m.reach) },
        { key: 'clicks', label: this.tr('Clicks'), icon: markRaw(MousePointerClick), value: formatCompactNumber(m.clicks) },
        {
          key: 'ctr',
          label: this.tr('CTR'),
          icon: markRaw(ChartLine),
          value: m.inline_link_click_ctr != null ? formatNumber(m.inline_link_click_ctr, '%', 2, true) : '–',
        },
        {
          key: 'roas',
          label: this.tr('ROAS'),
          icon: markRaw(ChartLine),
          value: m.purchase_roas != null ? formatNumber(m.purchase_roas, '×', 2, true) : '–',
        },
      ];

      if (m.video_views) {
        cards.push({ key: 'video_views', label: this.tr('Video views'), icon: markRaw(Eye), value: formatCompactNumber(m.video_views) });
      }

      return cards;
    },
  },
  watch: {
    metricPeriod() {
      this.loadMetrics();
    },
  },
  mounted() {
    if (this.isPublic) {
      this.metric = this.ad.metric ?? null;
      return;
    }

    this.loadPeriods();
    this.loadMetrics();
  },
  methods: {
    ucfirst,
    formatDateAndTime(date) {
      return `${formatToStringYMD(date, this.tr)} ${formatTime(date)}`.trim();
    },
    openSourcePost() {
      this.$fullscreenModal.open(this, ReportPostModal, { post: this.ad.post });
    },
    openEditModal() {
      if (this.isUpdatingStatus) {
        return;
      }

      if (!this.canEditAdText) {
        this.infoToast(this.tr('Text editing is only available for ads created as an ad creative.'));
        return;
      }

      this.$fullscreenModal.open(
        this,
        AdDetailsEditModal,
        { ad: this.ad },
        { closeWithOutclick: false },
      );
    },
    loadPeriods() {
      ReportService.getPeriods()
        .then(resp => {
          this.periods = resp?.periods ?? [];
          return null;
        })
        .catch(() => { /* the selector simply stays hidden */ });
    },
    loadMetrics() {
      if (!this.ad.id) {
        return;
      }

      this.isLoadingMetrics = true;
      AdService.getMetrics(this.ad.id, this.metricPeriod)
        .then(({ metric }) => {
          this.metric = metric;
          return null;
        })
        .catch(err => this.error(err))
        .finally(() => this.isLoadingMetrics = false);
    },
  },
};
</script>
